import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import S3FileUpload from "react-s3/lib/ReactS3";
import { s3Config } from "../config/s3bucket";
import { compressImage } from "../utils";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

export const useBranchValidation = (initialState, isEdit = false) => {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const {
    ready,
    value,
    suggestions: { status, data: suggestionsData },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
    },
    debounce: 300,
    initOnMount: false,
  });

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      init();
    } else {
      const interval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          init();
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [init]);

  const validateBranch = (dataToValidate) => {
    const newErrors = {};
    let isValid = true;

    if (!dataToValidate.branchName || !dataToValidate.branchName.trim()) {
      newErrors.branchName = "*Branch name is required";
      isValid = false;
    }

    if (!dataToValidate.phoneNumber || !dataToValidate.phoneNumber.trim()) {
      newErrors.phoneNumber = "*Phone number is required";
      isValid = false;
    }

    if (!dataToValidate.address || !dataToValidate.address.trim()) {
      newErrors.address = "*Address is required";
      isValid = false;
    }

    if (!dataToValidate.city || !dataToValidate.city.trim()) {
      newErrors.city = "*City is required";
      isValid = false;
    }

    if (!dataToValidate.managerName || !dataToValidate.managerName.trim()) {
      newErrors.managerName = "*Manager name is required";
      isValid = false;
    }

    if (!dataToValidate.loginEmail || !dataToValidate.loginEmail.trim()) {
      newErrors.loginEmail = "*Login email is required";
      isValid = false;
    } else if (!/^.+?@.+?\..+$/.test(dataToValidate.loginEmail)) {
      newErrors.loginEmail = "*Email format is not valid";
      isValid = false;
    }

    if (!isEdit) {
      if (!dataToValidate.password) {
        newErrors.password = "*Password is required";
        isValid = false;
      } else if (dataToValidate.password.length < 8) {
        newErrors.password = "*Password should not be less than 8 characters";
        isValid = false;
      }
    } else {
      if (dataToValidate.password && dataToValidate.password.length < 8) {
        newErrors.password = "*Password should not be less than 8 characters";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setValue(val);
    setFormData((prev) => ({
      ...prev,
      address: val,
      city: val ? prev.city : "",
      lat: val ? prev.lat : "",
      long: val ? prev.long : "",
    }));
  };

  const handleSelect =
    ({ description }) =>
    () => {
      setValue(description, false);
      clearSuggestions();

      setFormData((prev) => ({
        ...prev,
        address: description,
      }));

      getGeocode({ address: description })
        .then((results) => {
          const { lat, lng } = getLatLng(results[0]);
          let cityVal = "";

          if (results[0] && results[0].address_components) {
            const addressComponents = results[0].address_components;
            for (let i = 0; i < addressComponents.length; i++) {
              const component = addressComponents[i];
              if (component.types.includes("locality")) {
                cityVal = component.long_name;
                break;
              } else if (component.types.includes("administrative_area_level_2")) {
                cityVal = component.long_name;
              } else if (component.types.includes("administrative_area_level_1")) {
                cityVal = component.long_name;
              }
            }
          }

          setFormData((prev) => ({
            ...prev,
            city: cityVal || "Riyadh",
            lat: lat,
            long: lng,
          }));
        })
        .catch((error) => {
          console.error("Error geocoding address:", error);
        });
    };

  const handleImageChange = async (e) => {
    const file = e?.target?.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("Only jpeg, jpg and png are allowed", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    try {
      setIsUploading(true);
      const compressedFile = await compressImage(file, 0.7, 1024);
      const data = await S3FileUpload.uploadFile(compressedFile, s3Config);

      setFormData((prev) => ({
        ...prev,
        logo: data.location,
        bannerImage: file,
      }));
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Image upload failed", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    isUploading,
    validateBranch: () => validateBranch(formData),
    handleChange,
    handleInput,
    handleSelect,
    handleImageChange,
    autocomplete: {
      ready,
      value,
      status,
      data: suggestionsData,
      setValue,
      clearSuggestions,
    },
  };
};
